// src/modules/app-views/goodsReceipt/pages/Document/tabs/RecordTab/TotalsCard.jsx
// *******************************************************************************
// GOODS RECEIPT TOTALS CARD (RESUMO DA NOTA)
// *******************************************************************************
// Displays totals summary: products, freight, insurance, discount, taxes, total.
// Editable fields: freightAmount, insuranceAmount, discountAmount, otherExpenses.
// Tax fields are read-only (calculated from items or imported from XML).
// Uses Descriptions bordered for structured data display with responsiveness.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Row, Col, Descriptions, Divider, Typography } from 'antd';
import SimpleCurrency from 'components/SimpleComponents/SimpleCurrency';
import translateX from 'utils/translateX';
import financialService from 'services/financialService';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const TotalsCard = ({ items = [], data, viewMode }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isLocked = viewMode === 'view';
  const isConfirmed = data?.status === 'confirmed';

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // Calculate products total from items (live calculation)
  const productsTotal = useMemo(() => {
    return items.reduce((sum, item) => sum + (item.totalValue || 0), 0);
  }, [items]);

  // Use backend totals when available (imported or saved), fallback to items sum
  const totals = useMemo(() => {
    const t = data?.totals || {};
    return {
      productsAmount: t.productsAmount || productsTotal,
      freightAmount: t.freightAmount || 0,
      insuranceAmount: t.insuranceAmount || 0,
      discountAmount: t.discountAmount || 0,
      otherExpensesAmount: t.otherExpensesAmount || 0,
      // Tax totals (read-only)
      icmsBaseAmount: t.icmsBaseAmount || 0,
      icmsAmount: t.icmsAmount || 0,
      icmsStBaseAmount: t.icmsStBaseAmount || 0,
      icmsStAmount: t.icmsStAmount || 0,
      ipiAmount: t.ipiAmount || 0,
      pisAmount: t.pisAmount || 0,
      cofinsAmount: t.cofinsAmount || 0,
      iiAmount: t.iiAmount || 0,
      approximateTaxes: t.approximateTaxes || 0,
      documentAmount: t.documentAmount || 0,
    };
  }, [data?.totals, productsTotal]);

  // Check which tax fields have values (to render conditionally)
  const hasIcmsSt = totals.icmsStAmount > 0 || totals.icmsStBaseAmount > 0;
  const hasImportTax = totals.iiAmount > 0;
  const hasApproxTaxes = totals.approximateTaxes > 0;

  // Build Descriptions items for taxes (conditional rendering)
  const taxItems = useMemo(() => {
    const items = [
      { key: 'productsAmount', label: translateX('products_total'), children: <Text strong>{financialService.formatCurrency(totals.productsAmount)}</Text> },
      { key: 'icmsBase', label: translateX('icms_base'), children: financialService.formatCurrency(totals.icmsBaseAmount) },
      { key: 'icms', label: 'ICMS', children: financialService.formatCurrency(totals.icmsAmount) },
      { key: 'ipi', label: 'IPI', children: financialService.formatCurrency(totals.ipiAmount) },
      { key: 'pis', label: 'PIS', children: financialService.formatCurrency(totals.pisAmount) },
      { key: 'cofins', label: 'COFINS', children: financialService.formatCurrency(totals.cofinsAmount) },
    ];

    if (hasImportTax) {
      items.push({ key: 'ii', label: translateX('ii_amount'), children: financialService.formatCurrency(totals.iiAmount) });
    }
    if (hasIcmsSt) {
      items.push({ key: 'icmsStBase', label: translateX('icms_st_base'), children: financialService.formatCurrency(totals.icmsStBaseAmount) });
      items.push({ key: 'icmsStAmount', label: translateX('icms_st_amount'), children: financialService.formatCurrency(totals.icmsStAmount) });
    }
    if (hasApproxTaxes) {
      items.push({ key: 'approxTaxes', label: translateX('approximate_taxes'), children: financialService.formatCurrency(totals.approximateTaxes) });
    }

    // Grand total as last item
    items.push({ key: 'documentAmount', label: <Text strong>{translateX('total_value')}</Text>, children: <Text strong>{financialService.formatCurrency(totals.documentAmount)}</Text> });

    return items;
  }, [totals, hasIcmsSt, hasImportTax, hasApproxTaxes]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Editable amounts: freight, insurance, discount, other expenses */}
      <Row gutter={[16, 0]}>
        <Col xs={12} md={6}>
          <SimpleCurrency
            name={['totals', 'freightAmount']}
            label="freight"
            disabled={isLocked || isConfirmed}
            min={0}
            currencies={[{ value: 'BRL', label: 'R$' }]}
          />
        </Col>
        <Col xs={12} md={6}>
          <SimpleCurrency
            name={['totals', 'insuranceAmount']}
            label="insurance"
            disabled={isLocked || isConfirmed}
            min={0}
            currencies={[{ value: 'BRL', label: 'R$' }]}
          />
        </Col>
        <Col xs={12} md={6}>
          <SimpleCurrency
            name={['totals', 'discountAmount']}
            label="discount"
            disabled={isLocked || isConfirmed}
            min={0}
            currencies={[{ value: 'BRL', label: 'R$' }]}
          />
        </Col>
        <Col xs={12} md={6}>
          <SimpleCurrency
            name={['totals', 'otherExpensesAmount']}
            label="other_expenses"
            disabled={isLocked || isConfirmed}
            min={0}
            currencies={[{ value: 'BRL', label: 'R$' }]}
          />
        </Col>
      </Row>

      <Divider style={{ margin: '8px 0 12px' }} />

      {/* Products total + Tax totals + Grand total (read-only, bordered) */}
      <Descriptions
        bordered
        size="small"
        column={{ xs: 1, sm: 2, md: 3 }}
        items={taxItems}
      />
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TotalsCard;
